import { CheckOutlined, Close, InfoOutlined, SvgIconComponent } from '@mui/icons-material';
import { Box, IconButton, SvgIconProps, Tooltip, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from './card';

export function NewInfoCard({
  bgColor,
  row,
  variant,
  size,
  color,
  illustration,
  illustrationWidth,
  disableIcon,
  Icon,
  iconColor,
  title,
  description,
  action,
  onClose,
}: {
  action?: ReactNode;
  bgColor?: string;
  color?: string;
  description?: ReactNode;
  disableIcon?: boolean;
  Icon?: ((props: SvgIconProps) => React.JSX.Element) | SvgIconComponent;
  iconColor?: string;
  illustration?: string;
  illustrationWidth?: number;
  onClose?: () => void;
  row?: boolean;
  size?: 'small';
  title?: ReactNode;
  variant?: 'info' | 'warn' | 'success' | 'error';
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card
      disableDivider
      sx={{
        bgcolor:
          bgColor ||
          (variant === 'info'
            ? '#f3f6ff'
            : variant === 'warn'
              ? '#fff5d0'
              : variant === 'success'
                ? '#e6fef3'
                : variant === 'error'
                  ? '#ffd7d7'
                  : undefined),
        containerType: 'inline-size',
        borderRadius: 2,
        width: '100%',
      }}
    >
      <Box
        alignItems={row ? 'center' : 'flex-start'}
        display="flex"
        flexDirection="row"
        gap={2}
        padding={size === 'small' ? 2 : 3}
        width="100%"
      >
        {illustration && (
          <img src={illustration} style={{ flexShrink: 0 }} width={illustrationWidth || 80} />
        )}
        <Box
          alignItems={{ '@': 'flex-start', '@500': row ? 'center' : 'flex-start' }}
          display="flex"
          flexDirection={{ '@': 'column', '@500': row ? 'row' : 'column' }}
          flexGrow={1}
          gap={2}
        >
          {variant === 'info' || variant === 'success'
            ? !Icon && !illustration && !disableIcon && <InfoOutlined color="primary" />
            : !description &&
              !illustration &&
              !disableIcon && <Avatar Icon={Icon} iconColor={iconColor} variant={variant} />}
          <Box
            alignItems={{ '@': 'flex-start', '@500': row ? 'center' : 'flex-start' }}
            display="flex"
            flexDirection={{ '@': 'column', '@500': row ? 'row' : 'column' }}
            flexGrow={1}
            gap={2}
            width="100%"
          >
            <Box
              display="flex"
              flexDirection={row || variant === 'info' || variant === 'success' ? 'row' : 'column'}
              flexGrow={row ? 1 : undefined}
              gap={(variant === 'info' && Icon) || description ? 2 : 1}
              width="100%"
            >
              {(onClose ||
                (variant === 'info' || variant === 'success'
                  ? !disableIcon && Icon
                  : description && !illustration && !disableIcon)) && (
                <Box alignItems="flex-start" display="flex" gap={3} justifyContent="space-between">
                  <Box alignItems="center" display="flex" flexShrink={0} height="100%">
                    {variant === 'info' || variant === 'success'
                      ? !disableIcon && Icon && <Icon color={variant} />
                      : description &&
                        !illustration &&
                        !disableIcon && (
                          <Avatar Icon={Icon} iconColor={iconColor} variant={variant} />
                        )}
                  </Box>
                  {onClose && (
                    <Box flexShrink={0}>
                      <Tooltip placement="left" title={t('commons.actions.close')}>
                        <IconButton onClick={onClose} size="small">
                          <Close />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}
              {(title || description) && (
                <Box
                  alignSelf={row ? 'center' : undefined}
                  display="flex"
                  flexDirection="column"
                  flexGrow={row ? 1 : undefined}
                  gap={row ? 0 : 1}
                >
                  {title && (
                    <Typography
                      color={color}
                      fontWeight={description ? 700 : undefined}
                      variant={size === 'small' ? 'body2' : 'body1'}
                    >
                      {title}
                    </Typography>
                  )}
                  {description && (
                    <Typography color={color} variant={size === 'small' ? 'body2' : 'body1'}>
                      {description}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            {action && <Box flexShrink={0}>{action}</Box>}
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

function Avatar({
  variant,
  Icon: _Icon,
  iconColor,
}: {
  Icon?: ((props: SvgIconProps) => React.JSX.Element) | SvgIconComponent;
  iconColor?: string;
  variant?: 'info' | 'warn' | 'success' | 'error';
}): React.JSX.Element {
  const Icon = _Icon || (variant === 'success' ? CheckOutlined : InfoOutlined);

  return (
    <Box
      alignItems="center"
      borderRadius={5}
      color={
        iconColor ||
        (variant === 'info'
          ? 'primary'
          : variant === 'success'
            ? '#048760'
            : variant === 'error'
              ? '#d34949'
              : '#bb8a1a')
      }
      display="flex"
      flexShrink={0}
      height={24}
      justifyContent="center"
      width={24}
    >
      <Icon color="inherit" />
    </Box>
  );
}
